export interface ResponsiveImageProps {
   src?: string;
   srcSet?: string;
   sizes?: string;
}

// An api/2.0 image object: a url per size name (mini, thumbnail, standard,
// large, huge, …), plus assorted non-url metadata. Kept permissive because the
// wire data is loosely shaped.
export interface SizedImage {
   [sizeName: string]: string | number | undefined;
}

/**
 * Responsive <img> props (src/srcSet/sizes) for an api/2.0 image object, which
 * carries a url per size name. The single TypeScript home for what the guide
 * front end otherwise hand-rolls in several places (the vanilla embed's
 * `imgSrcSet`, `ViewUtils.php`) — see the duplication finding in
 * iFixit/ifixit#62872. Returns a props object to spread onto an <img> instead
 * of an HTML attribute string.
 *
 * @param image    size-name -> url map (mini, thumbnail, standard, large, …)
 * @param viewSize `sizes` hint for the browser; defaults to 100vw
 * @param maxSize  largest size name to include (default 'large')
 */
export function imgSrcSet(
   image: SizedImage | null | undefined,
   viewSize?: string,
   maxSize: string = 'large'
): ResponsiveImageProps {
   const sizeWidths: Record<string, number> = { ...App.imageSizeWidths };
   // A mini is tiny; never worth emitting on its own.
   delete sizeWidths.mini;

   const maxWidth = sizeWidths[maxSize];
   const candidates: { url: string; width: number }[] = [];
   for (const sizeName of Object.keys(sizeWidths)) {
      const url = image?.[sizeName];
      if (sizeWidths[sizeName] <= maxWidth && typeof url === 'string') {
         candidates.push({ url, width: sizeWidths[sizeName] });
      }
   }

   if (!candidates.length) {
      // No sized renditions — fall back to whatever single url exists.
      const fallback = image && typeof image.large === 'string' ? image.large : undefined;
      return { src: fallback };
   }

   // iOS 8 Safari reads only the first width-based srcset entry, so order
   // largest-first to default to the biggest image there.
   candidates.sort((a, b) => b.width - a.width);

   return {
      src: candidates[0].url,
      srcSet: candidates.map(c => `${c.url} ${c.width}w`).join(', '),
      sizes: viewSize || '100vw',
   };
}
